/**
 * Created by Ning Jiang on 11/13/2017.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IScreenViewConfig, ScreenView} from "@nolimitcity/slot-game/bin/core/screen/ScreenView";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite, TweenMax} from "gsap";
import {BackgroundAnimationsController} from "../../background/BackgroundAnimationsController";
import {ShakingAnimation} from "../../displayobject/ShakingAnimation";
import {FreespinsLeftIndicator} from "../../freespinsleft/FreespinsLeftIndicator";
import {WildNudgeSoundConfig} from "../../WildNudgeSoundConfig";

export class WildNudgeFreespinIntroView extends ScreenView {

    private _bgLights:PIXI.Sprite;
    private _fsTextStillImage:ShakingAnimation;
    private _fsTextAnimation:TimelineSprite;
    private _sevenTextImage:ShakingAnimation;
    private _smokeBurst:TimelineSprite;
    private _container:PIXI.Container;

    private _mainTimeline:TimelineLite;

    constructor(config:IScreenViewConfig, autoClose:boolean = true) {
        super(config, autoClose);
    }

    protected initAnimations():void {
        super.initAnimations();
        this.resetScreen();
    }

    protected createGameGraphics():PIXI.DisplayObject {
        this._container = new PIXI.Container();

        this._bgLights = new PIXI.Sprite(GameResources.getTextures("freespinLights")[0]);
        this._bgLights.anchor.set(0.5, 0.5);
        this._bgLights.blendMode = PIXI.BLEND_MODES.ADD;

        this._fsTextStillImage = new ShakingAnimation(GameResources.getTextures("freespinsText")[0]);
        this._fsTextStillImage.anchor.set(0.5, 0.5);
        this._fsTextStillImage.blendMode = PIXI.BLEND_MODES.NORMAL;

        this._fsTextAnimation = new TimelineSprite(GameResources.getTextures("freespinFire"));
        this._fsTextAnimation.anchor.set(0.5, 0.5);
        this._fsTextAnimation.blendMode = PIXI.BLEND_MODES.ADD;

        this._sevenTextImage = new ShakingAnimation(GameResources.getTextures("freespinSeven")[0]);
        this._sevenTextImage.anchor.set(0.5, 0.5);

        this._smokeBurst = new TimelineSprite(GameResources.getTextures("wild_smokeBurst"));
        this._smokeBurst.anchor.set(0.5, 0.5);
        this._smokeBurst.scale.set(2);
        this._smokeBurst.blendMode = PIXI.BLEND_MODES.ADD;

        this._container.addChild(this._bgLights);
        this._container.addChild(this._fsTextStillImage);
        this._container.addChild(this._fsTextAnimation);
        this._container.addChild(this._sevenTextImage);
        this._container.addChild(this._smokeBurst);

        return this._container;
    }

    protected onResize(resizeData:IResizeData):void {
        super.onResize(resizeData);
        this._container.position.set(this._screenWidth * 0.5, this._screenHeight * 0.5);
    }

    protected startPresentation(data?:ParsedServerData, onShowComplete?:() => void):void {
        if(!this._mainTimeline) {
            this._mainTimeline = new TimelineLite({paused : true});

            this._mainTimeline.add([
                () => {
                    EventHandler.dispatchEvent(new GameEvent(BackgroundAnimationsController.SET_FLASH_LIGHTS, {
                        val : true,
                        fps : 30
                    }));
                    EventHandler.dispatchEvent(new GameEvent(FreespinsLeftIndicator.FS_ANTICIPATION_LIGHTS_ON));
                    SlotGame.sound.playEffect(WildNudgeSoundConfig.instance.FREE_SPIN_INTRO);
                },
                // fade in background light.
                TweenLite.fromTo(this._bgLights, 0.6, {alpha : 0}, {alpha : 1})
            ]);

            const textStartPoint:number = this._mainTimeline.duration();
            this._mainTimeline.add([
                this.createFsTextStillImageTimeline(),
                this.createFsTextAnimationTimeline(),
                this._fsTextAnimation.getAnimationAutoShowHide(),
                this.createSevenTimeline(() => {if(onShowComplete) onShowComplete()})
            ], textStartPoint);

            // Fade bgLight again and start blink when FsTextAnimationComplete.
            this._mainTimeline.add(this.createBgLightSecondTimeline(), textStartPoint + 0.2);
        }

        this._mainTimeline.play(0);
    }

    private createFsTextStillImageTimeline():TimelineLite {
        const timeline:TimelineLite = new TimelineLite({onStart : () => this._fsTextStillImage.visible = true});
        timeline.add(TweenLite.fromTo(this._fsTextStillImage.scale, 0.2, {x : 0.3, y : 0.3}, {x : 2, y : 2}));
        timeline.add(TweenLite.to(this._fsTextStillImage.scale, 0.2, {x : 1, y : 1}));
        timeline.add(() => this._fsTextStillImage.shake(0.1, 3));

        return timeline;
    }

    private createFsTextAnimationTimeline():TimelineLite {
        const timeline:TimelineLite = new TimelineLite({onStart : () => this._fsTextAnimation.visible = true});
        timeline.add(TweenLite.fromTo(this._fsTextAnimation.scale, 0.2, {x : 0.6, y : 0.6}, {x : 4, y : 4}));
        timeline.add(TweenLite.to(this._fsTextAnimation.scale, 0.2, {x : 2, y : 2}));

        return timeline;
    }

    private createBgLightSecondTimeline():TimelineLite {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(TweenLite.to(this._bgLights, this._fsTextAnimation.totalDuration - 0.2, {alpha : 0.3}));
        timeline.add([
            TweenLite.to(this._bgLights, 2, {alpha : 1}),
            () => EventHandler.dispatchEvent(new GameEvent(BackgroundAnimationsController.START_CONTINIOUS_ANIMATIONS))
        ]);
        timeline.add(TweenMax.to(this._bgLights, 0.5, {alpha : 8, repeat : -1, yoyo : true}));

        return timeline;
    }

    private createSevenTimeline(onCompleteFunc:() => void):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => this._sevenTextImage.visible = true,
            TweenLite.fromTo(this._sevenTextImage.scale, 0.2, {x : 3, y : 3}, {x : 0.8, y : 0.8})
        ], this._fsTextAnimation.totalDuration - 0.4);
        timeline.add(TweenLite.to(this._sevenTextImage.scale, 0.2, {x : 1, y : 1}));
        timeline.add(() => {
            this._sevenTextImage.shake(0.07, 3);
            // this._autoCloseTimer.visible = true;
            this._button.visible = true;
            if(onCompleteFunc) {
                onCompleteFunc();
            }
        });

        //plays smokeBurst animation after number seven lands in
        timeline.add(this._smokeBurst.getAnimationAutoShowHide(), this._fsTextAnimation.totalDuration - 0.3);

        return timeline;
    }

    protected stopPresentation(closeCallback?:() => void) {
        EventHandler.dispatchEvent(new GameEvent(BackgroundAnimationsController.STOP_CONTINIOUS_ANIMATIONS));

        this._mainTimeline.pause();
        this._mainTimeline.kill();
        this._fsTextStillImage.stopShake();
        this._sevenTextImage.stopShake();
        this.resetScreen();

        super.stopPresentation(closeCallback);
    }

    private resetScreen():void {
        this._bgLights.alpha = 0;
        this._fsTextStillImage.visible = false;
        this._fsTextAnimation.visible = false;
        this._sevenTextImage.visible = false;
        this._smokeBurst.visible = false;

        // this._autoCloseTimer.visible = false;
        this._button.visible = false;
    }
}